import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import StaffScheduleManager from './StaffScheduleManager';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const C = { bg:'#0a0a12',surface:'#12121f',border:'#1e1e35',purple:'#a78bfa',green:'#3ecf8e',orange:'#ff6b2b',text:'#e2e2e8',muted:'#666' };

export default function StaffCalendar() {
  const [staff,setStaff]=useState([]);
  const [schedules,setSchedules]=useState({});
  const [loading,setLoading]=useState(true);
  const [scheduleStaff,setScheduleStaff]=useState(null);
  const [showAdd,setShowAdd]=useState(false);
  const [newStaff,setNewStaff]=useState({first_name:'',last_name:'',email:'',phone:'',role:'Therapist',commission_rate:35});
  const todayDow=new Date().getDay();

  useEffect(()=>{loadAll();},[]);

  const loadAll=async()=>{
    try{
      setLoading(true);
      const res=await axios.get('/api/staff');
      const list=res.data.staff||[];
      setStaff(list);
      const map={};
      await Promise.all(list.map(async s=>{
        try{ const r=await axios.get(`/api/staff-schedules/${s.id}`); map[s.id]=r.data.schedules||[]; }
        catch{ map[s.id]=[]; }
      }));
      setSchedules(map);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  };

  const addStaff=async()=>{
    if(!newStaff.first_name)return alert('First name required');
    try{
      await axios.post('/api/staff',newStaff);
      setShowAdd(false);
      setNewStaff({first_name:'',last_name:'',email:'',phone:'',role:'Therapist',commission_rate:35});
      loadAll();
    }catch(e){alert('Error: '+(e.response?.data?.error||e.message));}
  };

  const isWorking=id=>(schedules[id]||[]).find(s=>s.day_of_week===todayDow)?.is_working;
  const todayHours=id=>{
    const s=(schedules[id]||[]).find(s=>s.day_of_week===todayDow);
    if(!s?.is_working)return'Off today';
    return(s.start_time||'').substring(0,5)+' – '+(s.end_time||'').substring(0,5);
  };

  const inp={width:'100%',padding:'10px 14px',background:'#1a1c2e',border:'1px solid #1e1e35',borderRadius:8,color:C.text,fontSize:14,outline:'none',boxSizing:'border-box'};
  const lbl={fontSize:12,fontWeight:600,color:C.muted,marginBottom:4,display:'block'};

  if(loading)return<div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh',color:C.muted}}><p>Loading schedules...</p></div>;

  const working=staff.filter(s=>isWorking(s.id));
  const off=staff.filter(s=>!isWorking(s.id));

  return(
    <div style={{maxWidth:'100%',fontFamily:'inherit'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24,flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontSize:26,fontWeight:800,color:'#fff',margin:'0 0 6px'}}>Staff Schedule</h1>
          <p style={{fontSize:14,color:C.muted,margin:0}}>{staff.length} staff · {working.length} working today</p>
        </div>
        <button onClick={()=>setShowAdd(true)} style={{padding:'10px 20px',background:'linear-gradient(135deg,#ff6b2b,#ffab40)',border:'none',borderRadius:10,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>+ Add Staff</button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:24}}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.green,textTransform:'uppercase',letterSpacing:1,marginBottom:12}}>Working Today</div>
          {working.length===0?<div style={{color:C.muted,fontSize:13}}>No staff scheduled</div>:working.map(s=>(
            <div key={s.id} style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
              <span style={{color:C.text,fontSize:14,fontWeight:600}}>{s.first_name} {s.last_name}</span>
              <span style={{color:C.muted,fontSize:12}}>{todayHours(s.id)}</span>
            </div>
          ))}
        </div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'#e8484a',textTransform:'uppercase',letterSpacing:1,marginBottom:12}}>Off Today</div>
          {off.length===0?<div style={{color:C.muted,fontSize:13}}>Everyone is working!</div>:off.map(s=>(
            <div key={s.id} style={{marginBottom:8}}><span style={{color:C.muted,fontSize:14}}>{s.first_name} {s.last_name}</span></div>
          ))}
        </div>
      </div>

      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden',marginBottom:24}}>
        <div style={{padding:'14px 20px',borderBottom:`1px solid ${C.border}`,fontWeight:700,color:C.text,fontSize:15}}>Weekly Schedule</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
            <thead>
              <tr style={{background:'#0e0e1e'}}>
                <th style={{padding:'10px 16px',textAlign:'left',color:C.muted,fontSize:12,fontWeight:700}}>Staff</th>
                {DAYS.map((d,i)=><th key={d} style={{padding:'10px 8px',textAlign:'center',color:i===todayDow?C.purple:C.muted,fontSize:12,fontWeight:700}}>{d}</th>)}
                <th style={{padding:'10px 16px',textAlign:'center',color:C.muted,fontSize:12,fontWeight:700}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.filter(s=>s.active!==false).map((s,idx)=>(
                <tr key={s.id} style={{borderTop:`1px solid ${C.border}`,background:idx%2===0?'transparent':'#0d0d1a'}}>
                  <td style={{padding:'12px 16px'}}>
                    <div style={{fontWeight:700,color:C.text,fontSize:14}}>{s.first_name} {s.last_name}</div>
                    <div style={{color:C.muted,fontSize:12}}>{s.role} · {s.commission_rate}%</div>
                  </td>
                  {DAYS.map((d,i)=>{
                    const sc=(schedules[s.id]||[]).find(x=>x.day_of_week===i);
                    return(
                      <td key={d} style={{padding:'8px',textAlign:'center'}}>
                        {sc?.is_working?(
                          <div style={{background:'rgba(167,139,250,0.1)',border:'1px solid rgba(167,139,250,0.3)',borderRadius:6,padding:'4px 6px'}}>
                            <div style={{fontSize:11,color:C.purple,fontWeight:700}}>{(sc.start_time||'').substring(0,5)}</div>
                            <div style={{fontSize:10,color:C.muted}}>{(sc.end_time||'').substring(0,5)}</div>
                          </div>
                        ):<div style={{color:'#333',fontSize:12}}>—</div>}
                      </td>
                    );
                  })}
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <button onClick={()=>setScheduleStaff(s)} style={{padding:'6px 14px',background:'rgba(167,139,250,0.15)',border:'1px solid rgba(167,139,250,0.3)',borderRadius:8,color:C.purple,fontSize:12,fontWeight:700,cursor:'pointer'}}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {scheduleStaff&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,width:'100%',maxWidth:680,maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:`1px solid ${C.border}`}}>
              <h2 style={{margin:0,color:C.text,fontSize:18,fontWeight:800}}>{scheduleStaff.first_name}'s Schedule</h2>
              <button onClick={()=>{setScheduleStaff(null);loadAll();}} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer'}}>x</button>
            </div>
            <div style={{padding:24}}>
              <StaffScheduleManager staffId={scheduleStaff.id} staffName={scheduleStaff.first_name+' '+scheduleStaff.last_name} onClose={()=>{setScheduleStaff(null);loadAll();}} />
            </div>
          </div>
        </div>
      )}

      {showAdd&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,width:'100%',maxWidth:480}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:`1px solid ${C.border}`}}>
              <h2 style={{margin:0,color:C.text,fontSize:18,fontWeight:800}}>Add Staff Member</h2>
              <button onClick={()=>setShowAdd(false)} style={{background:'none',border:'none',color:C.muted,fontSize:22,cursor:'pointer'}}>x</button>
            </div>
            <div style={{padding:24,display:'grid',gap:14}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={lbl}>First Name *</label><input style={inp} value={newStaff.first_name} onChange={e=>setNewStaff({...newStaff,first_name:e.target.value})} /></div>
                <div><label style={lbl}>Last Name</label><input style={inp} value={newStaff.last_name} onChange={e=>setNewStaff({...newStaff,last_name:e.target.value})} /></div>
              </div>
              <div><label style={lbl}>Email</label><input style={inp} value={newStaff.email} onChange={e=>setNewStaff({...newStaff,email:e.target.value})} /></div>
              <div><label style={lbl}>Phone</label><input style={inp} value={newStaff.phone} onChange={e=>setNewStaff({...newStaff,phone:e.target.value})} /></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={lbl}>Role</label>
                  <select style={inp} value={newStaff.role} onChange={e=>setNewStaff({...newStaff,role:e.target.value})}>
                    {['Lead Therapist','Massage Therapist','Esthetician','Nail Tech','Receptionist','Manager'].map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Commission %</label><input style={inp} type="number" value={newStaff.commission_rate} onChange={e=>setNewStaff({...newStaff,commission_rate:parseInt(e.target.value)})} /></div>
              </div>
              <button onClick={addStaff} style={{padding:'12px',background:'linear-gradient(135deg,#ff6b2b,#ffab40)',border:'none',borderRadius:10,color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',marginTop:8}}>Add Staff Member</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
